"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import type { JobItemDto, ResumeUploadSource } from "@artha/api-contracts";
import type { CandidatePersonalizationController } from "./useCandidatePersonalization";
import type {
  PersonalizationSheetStep,
  ResumeReviewForm,
} from "../personalization.types";
import {
  EMPTY_REVIEW_FORM,
  toEditableProfile,
  toReviewForm,
} from "../personalization.utils";
import { PersonalizationApiError } from "../services/personalizationClient";

type UseResumePersonalizationSheetInput = {
  onClose: () => void;
  onComplete?: () => void | Promise<void>;
  controller: CandidatePersonalizationController;
  initialStep?: PersonalizationSheetStep;
  uploadSource?: ResumeUploadSource;
  job?: JobItemDto | null;
};

export function useResumePersonalizationSheet({
  onClose,
  onComplete,
  controller,
  initialStep,
  uploadSource = "jobs_page",
  job,
}: UseResumePersonalizationSheetInput) {
  const t = useTranslations("profile.resumePersonalization");
  const [step, setStep] = useState<PersonalizationSheetStep>(() => {
    if (initialStep) return initialStep;
    return controller.isSessionLoading
      ? "parsing"
      : controller.isPersonalized
        ? "manage"
        : "upload";
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reviewForm, setReviewForm] =
    useState<ResumeReviewForm>(() =>
      controller.editableProfile
        ? toReviewForm(controller.editableProfile)
        : EMPTY_REVIEW_FORM,
    );
  const [signInEmail, setSignInEmail] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [fitAttemptId, setFitAttemptId] = useState<string | undefined>();
  const existingProfileFitStarted = useRef(false);
  const showFitFailure = (status: "no_match" | "unavailable") => {
    setStep(status);
  };

  const completeApply = useCallback(async () => {
    try {
      await onComplete?.();
      onClose();
    } catch {
      showFitFailure("unavailable");
    }
  }, [onClose, onComplete]);

  const chooseFile = (file: File | null) => {
    setSelectedFile(file);
    setLocalError(null);
  };

  const upload = async () => {
    if (!selectedFile) {
      setLocalError(t("errors.chooseResume"));
      return;
    }
    setStep("parsing");
    setLocalError(null);
    try {
      const result = await (async () => {
        if (uploadSource !== "apply_gate") {
          return controller.uploadResume(selectedFile);
        }
        return job
          ? controller.uploadResume(selectedFile, uploadSource, job)
          : controller.uploadResume(selectedFile, uploadSource);
      })();
      if (result.editableProfile) {
        setReviewForm(toReviewForm(result.editableProfile));
      }
      setStep("review");
    } catch {
      setStep("upload");
    }
  };

  const checkApplyFit = useCallback(async (profile: ReturnType<typeof toEditableProfile>) => {
    setVerificationEmail(profile.email);
    try {
      const result = await controller.requestApplyFitVerification(profile, {
        source: "apply_gate",
        ...(job ? { job } : {}),
      });
      if (result.applyGate?.fitAttemptId) {
        setFitAttemptId(result.applyGate.fitAttemptId);
      }
      if (result.applyGate?.status === "no_match") {
        if (result.verificationRequired) {
          setStep("otp");
          return;
        }
        showFitFailure("no_match");
        return;
      }
      if (result.applyGate?.status === "unavailable") {
        if (result.verificationRequired) {
          setStep("otp");
          return;
        }
        showFitFailure("unavailable");
        return;
      }
      if (result.applyGate?.status === "match") {
        if (result.verificationRequired) {
          setStep("otp");
          return;
        }
        await completeApply();
        return;
      }
      showFitFailure("unavailable");
    } catch {
      showFitFailure("unavailable");
    }
  }, [completeApply, controller, job]);

  useEffect(() => {
    if (
      uploadSource !== "apply_gate" ||
      initialStep !== "parsing" ||
      !controller.isAuthenticated ||
      !controller.editableProfile ||
      existingProfileFitStarted.current
    ) {
      return;
    }
    existingProfileFitStarted.current = true;
    void checkApplyFit(controller.editableProfile);
  }, [checkApplyFit, controller.editableProfile, controller.isAuthenticated, initialStep, uploadSource]);

  const review = async () => {
    const profile = toEditableProfile(reviewForm);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      setLocalError(t("errors.invalidEmail"));
      return;
    }
    if (
      profile.titles.length === 0 &&
      profile.skills.length === 0 &&
      profile.keywords.length === 0
    ) {
      setLocalError(t("errors.missingPreferences"));
      return;
    }
    setLocalError(null);
    setVerificationEmail(profile.email);
    if (uploadSource === "apply_gate") {
      await checkApplyFit(profile);
    } else {
      try {
        const verificationRequired =
          await controller.requestProfileVerification(profile, {
            source: uploadSource,
          });
      if (verificationRequired) {
        setStep("otp");
      } else {
        await onComplete?.();
        onClose();
      }
      } catch {
      setStep("review");
      }
    }
  };

  const signIn = async () => {
    const email = signInEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError(t("errors.invalidEmail"));
      return;
    }
    setLocalError(null);
    setVerificationEmail(email);
    setStep("otp");
    try {
      const result = await controller.requestSignIn(email, {
        source: "jobs_page",
      });
      if (!result.verificationRequired) {
        if (uploadSource === "apply_gate" && result.editableProfile) {
          setReviewForm(toReviewForm(result.editableProfile));
          setStep("parsing");
          await checkApplyFit(result.editableProfile);
        } else {
          onClose();
        }
      }
    } catch (error) {
      if (
        error instanceof PersonalizationApiError &&
        error.code === "CANDIDATE_PROFILE_NOT_FOUND"
      ) {
        setLocalError(t("errors.profileNotFound"));
        setStep("upload");
        return;
      }
      setStep("sign_in");
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) {
      setLocalError(t("errors.invalidCode"));
      return;
    }
    setLocalError(null);
    try {
      const session = await controller.verifyCode(
        verificationEmail,
        code,
        fitAttemptId,
      );
      if (
        uploadSource === "apply_gate" &&
        controller.verificationMode === "login"
      ) {
        if (session.editableProfile) {
          setReviewForm(toReviewForm(session.editableProfile));
          setStep("parsing");
          await checkApplyFit(session.editableProfile);
        } else {
          setStep("upload");
        }
      } else if (uploadSource === "apply_gate" && session.applyGate?.status) {
        if (session.applyGate.status === "match") {
          await completeApply();
        } else {
          showFitFailure(session.applyGate.status);
        }
      } else if (
        uploadSource !== "apply_gate" &&
        (session.hasResume || Boolean(session.editableProfile))
      ) {
        await onComplete?.();
        onClose();
      } else if (uploadSource === "apply_gate") {
        showFitFailure("unavailable");
      } else setStep("upload");
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const resend = async () => {
    setLocalError(null);
    try {
      if (controller.verificationMode === "login") {
        await controller.requestSignIn(verificationEmail, {
          source: "jobs_page",
        });
      } else {
        if (uploadSource === "apply_gate") {
          const result = await controller.requestApplyFitVerification(
            toEditableProfile(reviewForm),
            {
              source: uploadSource,
              ...(job ? { job } : {}),
              fitAttemptId,
            },
          );
          if (result.applyGate?.fitAttemptId) {
            setFitAttemptId(result.applyGate.fitAttemptId);
          }
      } else {
        await controller.requestProfileVerification(
          toEditableProfile(reviewForm),
          { source: uploadSource },
        );
      }
      }
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const goBack = () => {
    setStep(
      step === "otp"
        ? controller.verificationMode === "login"
          ? "sign_in"
          : "review"
        : "upload",
    );
  };

  const showUpload = () => {
    setLocalError(null);
    setSelectedFile(null);
    setStep("upload");
  };

  const logout = async () => {
    try {
      await controller.logout();
      setStep("upload");
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const retryFit = () => {
    controller.captureJobFitEvent("retry");
    setFitAttemptId(undefined);
    setStep("review");
  };

  return {
    step,
    selectedFile,
    reviewForm,
    signInEmail,
    verificationEmail,
    code,
    error: localError || controller.error,
    showBack: step === "review" || step === "sign_in" || step === "otp",
    chooseFile,
    setReviewForm,
    setSignInEmail,
    setCode,
    setStep,
    upload,
    review,
    signIn,
    verify,
    resend,
    goBack,
    showUpload,
    logout,
    retryFit,
  };
}
